import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, Phone } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "March 6, 2026";

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-stone-100 py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Introduction */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Introduction</h2>
          <p className="text-gray-700 leading-relaxed">
            Lone Star Republic Consulting ("we," "us," or "our") respects your privacy and is committed 
            to protecting your personal information. This Privacy Policy explains how we collect, use, 
            and safeguard information when you visit our website or contact us for services.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Information We Collect</h2>
          
          <h3 className="font-semibold text-gray-900 mb-2">Information You Provide</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            When you contact us through our website or by other means, we may collect:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6 ml-4">
            <li>Your name</li>
            <li>Organization or business name</li>
            <li>Email address</li>
            <li>Phone number</li>
            <li>Message content and any details you choose to share about your needs</li>
          </ul>
          
          <h3 className="font-semibold text-gray-900 mb-2">Information Collected Automatically</h3>
          <p className="text-gray-700 leading-relaxed">
            Like most websites, we may automatically collect certain technical information when you visit, 
            including your IP address, browser type, and pages visited. This information helps us understand 
            how our website is used and improve your experience.
          </p>
        </section>

        {/* How We Use Your Information */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">How We Use Your Information</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We use the information we collect to:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Respond to your inquiries and provide the services you request</li>
            <li>Communicate with you about your questions or project needs</li>
            <li>Improve our website and services</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        {/* Information Sharing */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Information Sharing</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We do not sell, trade, or rent your personal information to third parties. We may share 
            information only in the following circumstances:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>With service providers who assist us in operating our website (under strict confidentiality agreements)</li>
            <li>When required by law or to protect our rights</li>
            <li>With your explicit consent</li>
          </ul>
        </section>

        {/* Data Security */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Data Security</h2>
          <p className="text-gray-700 leading-relaxed">
            We implement appropriate security measures to protect your personal information against 
            unauthorized access, alteration, disclosure, or destruction. However, no method of transmission 
            over the Internet is 100% secure, and we cannot guarantee absolute security.
          </p>
        </section>

        {/* Data Retention */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Data Retention</h2>
          <p className="text-gray-700 leading-relaxed">
            We retain your personal information only as long as necessary to fulfill the purposes for 
            which it was collected, or as required by law. Contact form submissions are retained to 
            ensure we can follow up on inquiries and maintain records of our communications.
          </p>
        </section>

        {/* Your Rights */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Your Rights</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            You have the right to:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Request access to the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your information (subject to legal requirements)</li>
            <li>Opt out of any marketing communications</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-4">
            To exercise these rights, please contact us using the information below.
          </p>
        </section>

        {/* Cookies */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Cookies</h2>
          <p className="text-gray-700 leading-relaxed">
            Our website may use cookies to enhance your browsing experience. Cookies are small files 
            stored on your device. You can configure your browser to refuse cookies, though this may 
            affect some website functionality.
          </p>
        </section>

        {/* Third-Party Links */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Third-Party Links</h2>
          <p className="text-gray-700 leading-relaxed">
            Our website may contain links to third-party websites. We are not responsible for the 
            privacy practices of these external sites. We encourage you to review the privacy policies 
            of any third-party sites you visit.
          </p>
        </section>

        {/* Children's Privacy */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Children's Privacy</h2>
          <p className="text-gray-700 leading-relaxed">
            Our services are not directed to individuals under 18 years of age. We do not knowingly 
            collect personal information from children.
          </p>
        </section>

        {/* Changes to This Policy */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Changes to This Policy</h2>
          <p className="text-gray-700 leading-relaxed">
            We may update this Privacy Policy from time to time. Any changes will be posted on this 
            page with an updated revision date. We encourage you to review this policy periodically.
          </p>
        </section>

        {/* Contact Us */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Contact Us</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="bg-stone-50 rounded-lg p-6 border border-gray-200">
            <div className="space-y-3">
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-[#c84630] mr-3" />
                <a href="mailto:hello@lonestarrepublicco.com" className="text-[#1e3a5f] hover:text-[#c84630]">
                  hello@lonestarrepublicco.com
                </a>
              </div>
              <div className="flex items-center">
                <Phone className="w-5 h-5 text-[#c84630] mr-3" />
                <a href="tel:325-305-2777" className="text-[#1e3a5f] hover:text-[#c84630]">
                  325-305-2777
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Back Link */}
        <div className="text-center pt-8 border-t border-gray-200">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#c84630] hover:text-[#a83825] font-medium"
          >
            ← Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
